import React, { useMemo, useRef } from 'react'

import classnames from 'classnames'

import { ChevronRightIcon } from '@toasttab/buffet-pui-icons'

import {
  AvailableFilters,
  CatalogBrand,
  CatalogCustomAttribute,
  CatalogCustomAttributeType,
  Menus_MenuItemTag,
  PriceRange,
  ProductOption
} from 'src/apollo/onlineOrdering'
import DropDown from 'src/shared/components/common/dropdown'
import { BrandedButton } from 'shared/components/common/button'
import { ButtonType } from 'src/apollo/sites'
import { t } from 'src/translations'
import ToggleSwitch from 'src/shared/components/common/forms/ToggleSwitch'

import useRefSize from 'src/lib/js/hooks/useRefSize'
import useInView from 'src/lib/js/hooks/useInView'
import useMoreItems, { MoreItemRefs } from 'shared/js/hooks/useMoreItems'

import { PriceRangeSlider } from './PriceRangeSlider'
import {
  BrandFilter,
  CountriesOfOriginFilter,
  CustomAttributeFilter,
  OptionFilter,
  TagsFilter
} from './filters'
import {
  FilterType,
  OrderedFilterGroup,
  filterLabel
} from './filterSettingsOrder'
import { useFilterToggle } from './useFilterToggle'

type FilterBarProps = {
  availableFilters?: AvailableFilters | null
  // Ref to the dropdown track owned by the parent so it stays mounted while
  // FilterBar measures how much space it has, truncating filters that overflow.
  parentRef: React.RefObject<HTMLDivElement>
  hideOutOfStockItems?: boolean | null
  // do any filters exist to show
  hasFilters: boolean
  // Tag/option groups in restaurant-configured order. Null when the store has no
  // filterSettings, in which case every available tag and option is shown with
  // tags before options (the default).
  orderedFilterGroups?: OrderedFilterGroup[] | null
}

type FilterDropdownButtonProps = {
  onClick: (e: React.MouseEvent) => void
  label: string
  expanded?: boolean
  labelId?: string
}

const FilterDropdownButton = ({
  onClick,
  label,
  expanded,
  labelId
}: FilterDropdownButtonProps) => {
  return (
    <BrandedButton
      onClick={onClick}
      variant={ButtonType.Secondary}
      iconRight={<ChevronRightIcon accessibility='decorative' />}
      className={classnames('dropdownButton', { expanded })}
    >
      <span id={labelId} className='dropdownButtonLabel'>
        {label}
      </span>
    </BrandedButton>
  )
}

type FilterDropdownProps = {
  label: string
  children: React.ReactNode
  labelId?: string
  // Identifies this dropdown's button/content for ARIA wiring and tests; without
  // it DropDown falls back to a shared id, so every filter would point at the
  // same content element.
  testId: string
  // Class for the content wrapper that stops clicks reaching DropDown (which closes on any content click).
  contentClassName: string
}

const FilterDropdown = ({
  label,
  children,
  labelId,
  testId,
  contentClassName
}: FilterDropdownProps) => (
  <DropDown
    label=''
    testId={testId}
    opener={(toggle, expanded) => (
      <FilterDropdownButton
        onClick={toggle}
        label={label}
        expanded={expanded}
        labelId={labelId}
      />
    )}
    ariaLabel={label}
    className='filterDropdown'
  >
    <div className={contentClassName} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </DropDown>
)

type FilterOptionsDropdownProps = {
  label: string
  // Prefix for each row's name/id, e.g. 'filter-brand'.
  name: string
  // Receives hasEntered (true once the dropdown has opened) so the shared filter
  // can defer its virtualizer until the content is measurable.
  children: (hasEntered: boolean) => React.ReactNode
}

// A dropdown wrapper for the shared list filters (Tags, Options, Brand, etc.).
// DropDown keeps its content mounted, so gate the virtualizer on real visibility
// (DropDown drops its `.hide` class on open) rather than tracking open state.
const FilterOptionsDropdown = ({
  label,
  name,
  children
}: FilterOptionsDropdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const hasEntered = useInView(containerRef)

  return (
    <DropDown
      label=''
      testId={name}
      ariaLabel={label}
      className='filterDropdown'
      contentRole='group'
      opener={(toggle, isExpanded) => (
        <FilterDropdownButton
          onClick={toggle}
          label={label}
          expanded={isExpanded}
        />
      )}
    >
      {/* Stop clicks/keys reaching DropDown, which closes on any content click — search and selection must keep it open. */}
      <div
        ref={containerRef}
        className='searchableFilterOptions'
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {children(hasEntered)}
      </div>
    </DropDown>
  )
}

const PriceFilter = ({ bounds }: { bounds: PriceRange }) => {
  const { selectedPriceRange, handlePriceRangeChange } = useFilterToggle()
  return (
    <FilterDropdown
      label={t('filters.price')}
      labelId='filter-bar-price-label'
      testId='filter-price'
      contentClassName='priceDropdownContent'
    >
      <PriceRangeSlider
        labelledById='filter-bar-price-label'
        bounds={bounds}
        value={selectedPriceRange ?? bounds}
        onChange={({ min, max }) => handlePriceRangeChange(min, max, bounds)}
      />
    </FilterDropdown>
  )
}

const AvailabilityFilter = () => {
  const { inStockOnly, handleInStockOnlyToggle } = useFilterToggle()
  return (
    <FilterDropdown
      label={t('filters.availability')}
      testId='filter-availability'
      contentClassName='availabilityDropdownContent'
    >
      <ToggleSwitch
        id='filterBarInStockOnlyToggle'
        checked={inStockOnly}
        onChange={handleInStockOnlyToggle}
        label={t('filters.in-stock-only')}
        labelPosition='left'
        className='filterToggle'
      />
    </FilterDropdown>
  )
}

const TagsDropdown = ({ tags }: { tags: Menus_MenuItemTag[] }) => {
  const { selectedTags, handleTagToggle } = useFilterToggle()
  return (
    <FilterOptionsDropdown label={t('filters.tags')} name='filter-tags'>
      {(hasEntered) => (
        <TagsFilter
          tags={tags}
          variant='bar'
          hasEntered={hasEntered}
          selectedTags={selectedTags}
          handleTagToggle={handleTagToggle}
        />
      )}
    </FilterOptionsDropdown>
  )
}

const OptionDropdown = ({ option }: { option: ProductOption }) => {
  const { selectedProductOptions, handleProductOptionToggle } =
    useFilterToggle()
  return (
    <FilterOptionsDropdown label={option.name} name={`filter-${option.name}`}>
      {(hasEntered) => (
        <OptionFilter
          option={option}
          variant='bar'
          hasEntered={hasEntered}
          selectedProductOptions={selectedProductOptions}
          handleProductOptionToggle={handleProductOptionToggle}
        />
      )}
    </FilterOptionsDropdown>
  )
}

const CustomAttributeDropdown = ({
  customAttribute
}: {
  customAttribute: CatalogCustomAttribute
}) => {
  const { selectedCustomAttributes, handleCustomAttributeToggle } =
    useFilterToggle()

  // Boolean attributes render as a single on/off switch (like AvailabilityFilter)
  // rather than a searchable list, so wrap the shared filter in the plain
  // FilterDropdown instead of the list-oriented FilterOptionsDropdown.
  if (customAttribute.type === CatalogCustomAttributeType.Boolean) {
    return (
      <FilterDropdown
        label={customAttribute.name}
        testId={`filter-${customAttribute.name}`}
        contentClassName='availabilityDropdownContent'
      >
        <CustomAttributeFilter
          attribute={customAttribute}
          variant='bar'
          hasEntered={false}
          selectedCustomAttributes={selectedCustomAttributes}
          handleCustomAttributeToggle={handleCustomAttributeToggle}
        />
      </FilterDropdown>
    )
  }
  return (
    <FilterOptionsDropdown
      label={customAttribute.name}
      name={`filter-${customAttribute.name}`}
    >
      {(hasEntered) => (
        <CustomAttributeFilter
          attribute={customAttribute}
          variant='bar'
          hasEntered={hasEntered}
          selectedCustomAttributes={selectedCustomAttributes}
          handleCustomAttributeToggle={handleCustomAttributeToggle}
        />
      )}
    </FilterOptionsDropdown>
  )
}

const BrandDropdown = ({ brands }: { brands: CatalogBrand[] }) => {
  const { selectedBrands, handleBrandToggle } = useFilterToggle()
  return (
    <FilterOptionsDropdown label={t('filters.brand')} name='filter-brand'>
      {(hasEntered) => (
        <BrandFilter
          brands={brands}
          variant='bar'
          hasEntered={hasEntered}
          selectedBrands={selectedBrands}
          handleBrandToggle={handleBrandToggle}
        />
      )}
    </FilterOptionsDropdown>
  )
}

const CountriesOfOriginDropdown = ({
  countryCodes
}: {
  countryCodes: string[]
}) => {
  const { selectedCountriesOfOrigin, handleCountryToggle } = useFilterToggle()
  return (
    <FilterOptionsDropdown
      label={t('filters.countries-of-origin')}
      name='filter-countries-of-origin'
    >
      {(hasEntered) => (
        <CountriesOfOriginFilter
          countryCodes={countryCodes}
          variant='bar'
          hasEntered={hasEntered}
          selectedCountriesOfOrigin={selectedCountriesOfOrigin}
          handleCountryToggle={handleCountryToggle}
        />
      )}
    </FilterOptionsDropdown>
  )
}

// One filter as plain data (like NavItems' { cta, ref } entries): a label, a
// measuring ref, and the node to render. Extends MoreItemRefs so the array
// feeds straight into useMoreItems — no separate measure array.
type FilterEntry = MoreItemRefs & {
  label: string
  node: React.ReactNode
}

// Gap (px) between dropdown buttons, used both to measure overflow and (via the
// --filter-dropdown-gap CSS var) to set the actual layout gap.
const DROPDOWN_GAP = 8

// How many dropdowns to show before the track is measured — enough to fill a
// typical desktop track; useMoreItems refines the count once measured.
const INITIAL_VISIBLE_DROPDOWNS = 3

const buildFilterDropdown = (
  group: OrderedFilterGroup
): { label: string; node: React.ReactNode } => {
  const label = filterLabel(group)
  switch (group.filterType) {
    case FilterType.Tags:
      return { label, node: <TagsDropdown tags={group.tags} /> }
    case FilterType.CustomAttribute:
      return {
        label,
        node: (
          <CustomAttributeDropdown customAttribute={group.customAttribute} />
        )
      }
    case FilterType.Option:
      return { label, node: <OptionDropdown option={group.option} /> }
    case FilterType.Brand:
      return { label, node: <BrandDropdown brands={group.brands} /> }
    case FilterType.CountriesOfOrigin:
      return {
        label,
        node: <CountriesOfOriginDropdown countryCodes={group.countryCodes} />
      }
  }
}

export const FilterBar = ({
  availableFilters,
  parentRef,
  hideOutOfStockItems,
  hasFilters,
  orderedFilterGroups
}: FilterBarProps) => {
  const filterEntries = useMemo<FilterEntry[]>(() => {
    const entries: FilterEntry[] = []

    const priceRange = availableFilters?.price
    if (priceRange && priceRange.min < priceRange.max) {
      entries.push({
        label: t('filters.price'),
        ref: React.createRef<HTMLDivElement>(),
        node: <PriceFilter bounds={priceRange} />
      })
    }

    if (!hideOutOfStockItems) {
      entries.push({
        label: t('filters.availability'),
        ref: React.createRef<HTMLDivElement>(),
        node: <AvailabilityFilter />
      })
    }

    // Tag/option groups in the restaurant's configured order. Empty when no
    // filterSettings are enabled, leaving only Price/Availability.
    const groups = orderedFilterGroups ?? []
    groups.forEach((group) => {
      const { label, node } = buildFilterDropdown(group)
      entries.push({ label, ref: React.createRef<HTMLDivElement>(), node })
    })

    return entries
  }, [availableFilters, hideOutOfStockItems, orderedFilterGroups])

  const { width } = useRefSize(parentRef)
  // moreWidth 0: overflow is handled by the parent's "view all filters" button
  const { viewItems, rerenderRef } = useMoreItems(
    filterEntries,
    0,
    DROPDOWN_GAP,
    width
  )

  if (!hasFilters) {
    return null
  }

  // Before the track is measured, useMoreItems returns an empty viewItems.
  // Show a few dropdowns whenever viewItems is empty so we never flash a blank filter bar
  const visibleEntries = (
    width == null || viewItems.length === 0
      ? filterEntries.slice(0, INITIAL_VISIBLE_DROPDOWNS)
      : viewItems
  ) as FilterEntry[]

  const filterDropdownGap = {
    '--filter-dropdown-gap': `${DROPDOWN_GAP}px`
  } as React.CSSProperties

  return (
    <>
      <div className='filterDropdowns' style={filterDropdownGap}>
        {visibleEntries.map((entry, index) => (
          <React.Fragment key={`view-filter-${index}`}>
            {entry.node}
          </React.Fragment>
        ))}
        <div className='hide' ref={rerenderRef} />
      </div>
      {/* Hidden mirror of every dropdown button for useMoreItems to measure.
          Matches the real DOM so widths line up, but skips the panel — only the
          button width matters. */}
      <div className='hiddenFilterDropdowns'>
        {filterEntries.map((entry, index) => (
          <div
            className='dropDownWrapper'
            key={`hidden-filter-${index}`}
            ref={entry.ref}
          >
            <div className='dropDown filterDropdown'>
              <FilterDropdownButton onClick={() => {}} label={entry.label} />
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
